import { useState } from "react";

import { EditPitchDialog } from "@/components/EditPitchDialog";
import { RowActions } from "@/components/tableKit";
import { toast } from "@/components/ui/toast";
import { analyticsService } from "../../../services/analytics";
import { approvePitchAsBooking, getUserById, rejectPitch, resubmitPitch, unrejectPitch, updateDoc, withdrawPitch, type FirestoreData } from "../../../services/firebase";
import type { Booking, Campaign, User } from "../../../shared/types";
import { isPitchReviewer, pitchActions, type PitchAction } from "../../../utils/pitching/permissions";
import { bookingFromApprovedPitch } from "../bookingFromPitch";
import type { PitchRow } from "../campaign";
import { NeedsInfoDialog } from "./NeedsInfoDialog";

export type PitchRowMenuProps = {
    row: PitchRow;
    user: User | undefined;
    campaign: Campaign;
    campaignId?: string;
    bookings: Booking[];
    booker?: User;
    onApproved: () => void;
};

/** Kebab, Needs info, and Edit pitch for one pitch row. */
export function PitchRowMenu({
    row,
    user,
    campaign,
    campaignId,
    bookings,
    booker,
    onApproved,
}: PitchRowMenuProps) {
    const [pending, setPending] = useState(false);
    const [needsInfoOpen, setNeedsInfoOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const talentAlreadyBooked = talentIsBooked(bookings, row.talentId) && !row.bookingId;
    const items = pitchActions(user, campaign, row, row.talent, talentAlreadyBooked);

    function choose(action: PitchAction) {
        if (pending) {
            return;
        }
        if (action === "needs-info") {
            setNeedsInfoOpen(true);
        } else if (action === "edit") {
            setEditOpen(true);
        } else if (action === "approve") {
            void approvePitch();
        } else if (action === "reject") {
            void setPitchStatus("reject");
        } else if (action === "unreject") {
            void setPitchStatus("unreject");
        } else if (action === "withdraw") {
            void setPitchStatus("withdraw");
        } else if (action === "resubmit") {
            void setPitchStatus("resubmit");
        }
    }

    async function setPitchStatus(action: PitchStatusAction) {
        if (row.bookingId) {
            return;
        }
        if ((action === "reject" || action === "withdraw") && row.status !== "pitched" && row.status !== "needs-info") {
            return;
        }
        if (action === "unreject" && row.status !== "rejected") {
            return;
        }
        if (action === "resubmit" && row.status !== "withdrawn") {
            return;
        }
        setPending(true);
        try {
            const result = await PITCH_STATUS_WRITE[action](row.id);
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: PITCH_STATUS_ERROR_TITLE[action],
                    description: "Refresh and try again.",
                });
            }
        } catch (error) {
            reportPitchError(error);
        } finally {
            setPending(false);
        }
    }

    async function approvePitch() {
        if (!campaignId || !row.talent) {
            return;
        }
        if (row.status !== "pitched" || row.bookingId || talentIsBooked(bookings, row.talentId)) {
            return;
        }
        setPending(true);
        try {
            let resolvedBooker = booker;
            if (!resolvedBooker && campaign.booker?.uid) {
                resolvedBooker = await getUserById(campaign.booker.uid);
            }
            const result = await approvePitchAsBooking(
                campaignId,
                row.id,
                bookingFromApprovedPitch({
                    campaign,
                    pitch: row,
                    talent: row.talent,
                    booker: resolvedBooker,
                    approver: user,
                }),
            );
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: "Couldn’t accept this pitch",
                    description: "Refresh and try again.",
                });
                return;
            }
            onApproved();
            toast.add({
                type: "success",
                title: "Pitch accepted",
                description: "A booking was created at this pitch’s price.",
            });
        } catch (error) {
            reportPitchError(error);
        } finally {
            setPending(false);
        }
    }

    async function submitNeedsInfo(note: Record<string, unknown>) {
        if (row.bookingId || !isPitchReviewer(user, campaign) || row.status !== "pitched") {
            return;
        }
        setPending(true);
        try {
            const result = await updateDoc("pitches", row.id, {
                status: "needs-info",
                needsInfoNote: note as FirestoreData,
            });
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: "Couldn’t request more information",
                    description: "Refresh and try again.",
                });
                return;
            }
            setNeedsInfoOpen(false);
        } catch (error) {
            reportPitchError(error);
        } finally {
            setPending(false);
        }
    }

    return (
        <>
            <RowActions
                actions={items.map((action) => ({
                    label: PITCH_ACTION_LABEL[action],
                    destructive: action === "reject" || action === "withdraw",
                    disabled: pending,
                    onSelect: () => choose(action),
                }))}
            />
            {needsInfoOpen ? (
                <NeedsInfoDialog
                    pitch={row}
                    actionPending={pending}
                    onOpenChange={(open) => {
                        if (!open) {
                            setNeedsInfoOpen(false);
                        }
                    }}
                    onSubmit={(_pitch, note) => {
                        void submitNeedsInfo(note);
                    }}
                />
            ) : null}
            {editOpen ? (
                <EditPitchDialog
                    pitch={row}
                    campaign={campaign}
                    user={user}
                    onOpenChange={(open) => {
                        if (!open) {
                            setEditOpen(false);
                        }
                    }}
                />
            ) : null}
        </>
    );
}

function talentIsBooked(bookings: Booking[], talentId: string) {
    return bookings.some((booking) => booking.talentId === talentId && !booking.deleted);
}

function reportPitchError(error: unknown) {
    console.error(error);
    analyticsService().logError(error);
    toast.add({
        type: "error",
        title: "Couldn’t update this pitch",
        description: "Refresh and try again.",
    });
}

const PITCH_ACTION_LABEL: Record<PitchAction, string> = {
    approve: "Approve",
    "needs-info": "Needs info",
    edit: "Edit pitch",
    withdraw: "Withdraw",
    reject: "Reject",
    unreject: "Unreject",
    resubmit: "Resubmit Pitch",
};

type PitchStatusAction = "reject" | "unreject" | "withdraw" | "resubmit";

const PITCH_STATUS_WRITE: Record<PitchStatusAction, (pitchId: string) => ReturnType<typeof rejectPitch>> = {
    reject: rejectPitch,
    unreject: unrejectPitch,
    withdraw: withdrawPitch,
    resubmit: resubmitPitch,
};

const PITCH_STATUS_ERROR_TITLE: Record<PitchStatusAction, string> = {
    reject: "Couldn’t reject this pitch",
    unreject: "Couldn’t reopen this pitch",
    withdraw: "Couldn’t withdraw this pitch",
    resubmit: "Couldn’t resubmit this pitch",
};
